import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from '../../components/form/AppSelect';
import { CenteredLoader, InlineLoader } from '../../components/LoadingSpinner';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Alert } from 'react-bootstrap';
import { api } from '../../shared/api';
import PageForm from '../../components/layout/PageForm';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import '../../shared/ReactSelectDarkMode.css';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const CrewForm = () => {
  const { crewId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(crewId);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    mobile: '',
    profession: null,
    company: null,
    user: null,
    default_hourly_rate: '',
    default_daily_rate: '',
  });

  const [professions, setProfessions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  const { isLoading: loading, data: initialData } = useQuery({
    queryKey: ['crew-form-init', crewId ?? 'new'],
    queryFn: async () => {
      let currentCrewMember = null;
      if (isEdit) {
        try { currentCrewMember = await api.get(`crew/api/crew/${crewId}/`); } catch {}
      }
      const [professionsResponse, companiesResponse, usersResponse, crewResponse] = await Promise.all([
        api.get('crew/api/professions/'),
        api.get('companies/api/companies/'),
        api.get('accounts/api/users/').catch(() => []),
        api.get('crew/api/crew/').catch(() => [])
      ]);
      const usersRaw = usersResponse?.results ?? usersResponse?.data?.results ?? usersResponse?.data ?? usersResponse ?? [];
      const allUsers = Array.isArray(usersRaw) ? usersRaw : [];
      const crewMembers = Array.isArray(crewResponse) ? crewResponse : [];
      const linkedUserIds = crewMembers
        .filter(c => c.user?.id && (!isEdit || c.id !== parseInt(crewId)))
        .map(c => c.user.id);
      let availableUsers = allUsers.filter(u => !linkedUserIds.includes(u.id));
      if (isEdit && currentCrewMember?.user) {
        const cu = allUsers.find(u => u.id === currentCrewMember.user.id);
        if (cu && !availableUsers.find(u => u.id === cu.id)) availableUsers.push(cu);
      }
      return { currentCrewMember, professions: professionsResponse || [], companies: companiesResponse || [], users: availableUsers };
    },
  });

  // Populate form and reference lists when initial data loads
  useEffect(() => {
    if (!initialData) return;
    setProfessions(initialData.professions);
    setCompanies(initialData.companies);
    setUsers(initialData.users);
    if (initialData.currentCrewMember) {
      const m = initialData.currentCrewMember;
      setFormData({
        first_name: m.first_name || m.display_first_name || '',
        last_name: m.last_name || m.display_last_name || '',
        email: m.email || m.display_email || '',
        telephone: m.company?.telephone || '',
        mobile: m.company?.mobile || '',
        profession: m.profession?.id || null,
        company: m.company?.id || null,
        user: m.user?.id || null,
        default_hourly_rate: m.default_hourly_rate ?? '',
        default_daily_rate: m.default_daily_rate ?? '',
      });
    }
  }, [initialData]);

  // Options derived from state (replaces 3 useEffect option-building hooks)
  const professionOptions = useMemo(() => professions.map(p => ({ value: p.id, label: p.name })), [professions]);
  const companyOptions = useMemo(() => companies.map(c => ({ value: c.id, label: c.company_name })), [companies]);
  const userOptions = useMemo(() => users.map(u => ({ value: u.id, label: `${u.username} (${u.email})` })), [users]);

  const saveMutation = useMutation({
    mutationFn: (submitData) => isEdit
      ? api.put(`crew/api/crew/${crewId}/`, submitData)
      : api.post('crew/api/crew/', submitData),
    onSuccess: (response) => {
      if (response.id) navigate(`/crew/${response.id}`);
      else navigate('/crew');
    },
    onError: (err) => {
      if (err.response?.data && typeof err.response.data === 'object') {
        setErrors(err.response.data);
      } else {
        setError(isEdit ? t('crew.errors.failedToUpdate') : t('crew.errors.failedToCreate'));
      }
    },
  });


  const fetchProfessions = async (searchTerm = '') => {
    try {
      let url = 'crew/api/professions/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setProfessions(response || []);
    } catch (err) {
      console.error('Error fetching professions:', err);
    }
  };

  const fetchCompanies = async (searchTerm = '') => {
    try {
      let url = 'companies/api/companies/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setCompanies(response || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchUsers = async (searchTerm = '') => {
    try {
      let url = 'accounts/api/users/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const [usersResponse, crewResponse] = await Promise.all([
        api.get(url),
        api.get('crew/api/crew/').catch(() => [])
      ]);

      // Handle paginated response structure for users: {results: [...], count: ...}
      let usersData = [];
      if (usersResponse.data && usersResponse.data.results) {
        usersData = usersResponse.data.results;
      } else if (usersResponse.data) {
        usersData = usersResponse.data;
      } else if (usersResponse.results) {
        usersData = usersResponse.results;
      } else {
        usersData = usersResponse;
      }

      // Filter users to exclude those already linked to crew members (except current one in edit mode)
      const allUsers = Array.isArray(usersData) ? usersData : [];
      const crewMembers = Array.isArray(crewResponse) ? crewResponse : [];

      const linkedUserIds = crewMembers
        .filter(crew => crew.user && crew.user.id && (!isEdit || crew.id !== parseInt(crewId)))
        .map(crew => crew.user.id);

      let availableUsers = allUsers.filter(user => !linkedUserIds.includes(user.id));

      // If editing and current crew member has a linked user, ensure it's included
      if (isEdit && formData.user) {
        const currentUser = allUsers.find(u => u.id === formData.user);
        if (currentUser && !availableUsers.find(u => u.id === currentUser.id)) {
          availableUsers.push(currentUser);
        }
      }

            setUsers(availableUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Debounced search functions
  const debouncedProfessionSearch = useCallback(
    debounce((inputValue) => {
      fetchProfessions(inputValue);
    }, 300),
    []
  );

  const debouncedCompanySearch = useCallback(
    debounce((inputValue) => {
      fetchCompanies(inputValue);
    }, 300),
    []
  );

  const debouncedUserSearch = useCallback(
    debounce((inputValue) => {
      fetchUsers(inputValue);
    }, 300),
    []
  );


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));

    // Clear field error when user makes a selection
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = t('crew.errors.firstNameRequired');
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t('crew.errors.lastNameRequired');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('crew.errors.invalidEmail');
    }

    if (!formData.profession) {
      newErrors.profession = t('crew.errors.professionRequired');
    }

      return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setError(null);

    const submitData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email || '',
      telephone: formData.telephone || '',
      mobile: formData.mobile || '',
      profession_id: formData.profession,
      company_id: formData.company || null,
      user_id: formData.user || null,
      default_hourly_rate: formData.default_hourly_rate !== '' ? formData.default_hourly_rate : 0,
      default_daily_rate: formData.default_daily_rate !== '' ? formData.default_daily_rate : 0,
    };

    saveMutation.mutate(submitData);
  };

  const submitting = saveMutation.isPending;

  if (loading) {
    return (
      <PageShell pretitle={t('crew.title')} title={isEdit ? t('crew.form.edit') : t('crew.form.create')}>
        <PageSection>
          <CenteredLoader message={t('crew.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  const backHref = isEdit ? `/crew/${crewId}` : '/crew';
  const pageTitle = isEdit ? t('crew.form.edit') : t('crew.form.create');

  return (
    <PageShell
      pretitle={t('crew.title')}
      title={pageTitle}
      description={t('crew.form.subtitle', 'Spravuj osobní údaje, profesi, výchozí sazby i propojení na uživatelský účet v jednom formuláři.')}
      actions={(
        <Link
          to={backHref}
          className="btn btn-outline-secondary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M5 12l14 0"></path>
            <path d="M5 12l6 6"></path>
            <path d="M5 12l6 -6"></path>
          </svg>
          {t('common.back')}
        </Link>
      )}
    >
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8">
          <PageForm
            title={isEdit ? t('crew.form.editCrewMember') : t('crew.form.newCrewMember')}
            description={t('crew.form.formDescription', 'Udržuj osobní, profesní a účetní údaje člena týmu v jednom sjednoceném editoru.')}
            onSubmit={handleSubmit}
            footer={
              <>
                <Link to={backHref} className="btn btn-outline-secondary">
                  {t('common.cancel')}
                </Link>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <InlineLoader className="me-2" />
                      {isEdit ? t('crew.form.updating') : t('crew.form.creating')}
                    </>
                  ) : (
                    isEdit ? t('common.update') : t('common.create')
                  )}
                </button>
              </>
            }
          >
            {error && (
              <Alert variant="danger" className="app-inline-alert mb-0" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <h4 className="form-section-title app-form-section-title">{t('crew.form.personalInfo', 'Osobní údaje')}</h4>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label required">{t('crew.form.firstName')}</label>
                  <input
                    type="text"
                    name="first_name"
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder={t('crew.form.firstNamePlaceholder')}
                    required
                  />
                  {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label required">{t('crew.form.lastName')}</label>
                  <input
                    type="text"
                    name="last_name"
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder={t('crew.form.lastNamePlaceholder')}
                    required
                  />
                  {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">{t('crew.form.email')}</label>
              <input
                type="email"
                name="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('crew.form.emailPlaceholder')}
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">{t('crew.form.mobile', 'Mobil')}</label>
                  <input
                    type="tel"
                    name="mobile"
                    className="form-control"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder={t('crew.form.mobilePlaceholder', '+420 ...')}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">{t('crew.form.telephone', 'Telefon')}</label>
                  <input
                    type="tel"
                    name="telephone"
                    className="form-control"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    placeholder={t('crew.form.telephonePlaceholder', '+420 ...')}
                  />
                </div>
              </div>
            </div>

            <h4 className="form-section-title app-form-section-title">{t('crew.form.professionalInfo')}</h4>

            <div className="mb-3">
              <label className="form-label required">{t('crew.form.profession')}</label>
              <Select
                name="profession"
                classNamePrefix="react-select"
                className={`react-select-container ${errors.profession ? 'is-invalid' : ''}`}
                value={professionOptions.find(option => option.value === formData.profession) || null}
                onChange={selectedOption => handleSelectChange('profession', selectedOption)}
                onInputChange={debouncedProfessionSearch}
                options={professionOptions}
                placeholder={t('crew.form.selectProfession')}
                isSearchable
                isClearable
                theme={selectTheme}
                noOptionsMessage={() => t('common.noOptions')}
                loadingMessage={() => t('common.loading')}
              />
              {errors.profession && <div className="invalid-feedback d-block">{errors.profession}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">{t('crew.form.company')}</label>
              <Select
                name="company"
                classNamePrefix="react-select"
                className={`react-select-container ${errors.company ? 'is-invalid' : ''}`}
                value={companyOptions.find(option => option.value === formData.company) || null}
                onChange={selectedOption => handleSelectChange('company', selectedOption)}
                onInputChange={debouncedCompanySearch}
                options={companyOptions}
                placeholder={t('crew.form.selectCompany')}
                isSearchable
                isClearable
                theme={selectTheme}
                noOptionsMessage={() => t('common.noOptions')}
                loadingMessage={() => t('common.loading')}
              />
              {errors.company && <div className="invalid-feedback d-block">{errors.company}</div>}
            </div>

            <h4 className="form-section-title app-form-section-title">{t('crew.form.defaultRates')}</h4>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">{t('crew.form.defaultHourlyRate')}</label>
                  <div className="input-group">
                    <input
                      type="number"
                      name="default_hourly_rate"
                      className="form-control"
                      value={formData.default_hourly_rate}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="any"
                    />
                    <span className="input-group-text">{t('crew.form.perHour')}</span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">{t('crew.form.defaultDailyRate')}</label>
                  <div className="input-group">
                    <input
                      type="number"
                      name="default_daily_rate"
                      className="form-control"
                      value={formData.default_daily_rate}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="any"
                    />
                    <span className="input-group-text">{t('crew.form.perDay')}</span>
                  </div>
                </div>
              </div>
            </div>

            <h4 className="form-section-title app-form-section-title">{t('crew.form.userAccount')}</h4>

            <div className="mb-3">
              <label className="form-label">{t('crew.form.linkUserAccount')}</label>
              <Select
                name="user"
                classNamePrefix="react-select"
                className={`react-select-container ${errors.user ? 'is-invalid' : ''}`}
                value={userOptions.find(option => option.value === formData.user) || null}
                onChange={selectedOption => handleSelectChange('user', selectedOption)}
                onInputChange={(inputValue) => {
                  if (inputValue) {
                    debouncedUserSearch(inputValue);
                  } else {
                    fetchUsers();
                  }
                }}
                options={userOptions}
                placeholder={userOptions.length > 0 ? t('crew.form.selectUser') : t('crew.form.noUsersAvailable')}
                isSearchable
                isClearable
                isDisabled={userOptions.length === 0}
                theme={selectTheme}
                noOptionsMessage={() => t('common.noOptions')}
                loadingMessage={() => t('common.loading')}
              />
              {errors.user && <div className="invalid-feedback d-block">{errors.user}</div>}
              <small className="form-text text-muted">
                {userOptions.length > 0 ? t('crew.form.userAccountHelp') : t('crew.form.allUsersLinked')}
              </small>
            </div>
          </PageForm>
        </div>
      </div>
    </PageShell>
  );
};

export default CrewForm;
